import {
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  HomeIcon,
  ArrowDownTrayIcon,
  HandRaisedIcon,
  CursorArrowRaysIcon,
  Squares2X2Icon,
  ViewfinderCircleIcon,
  QuestionMarkCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  SparklesIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
} from '@heroicons/react/24/outline'
import { useRef, useEffect, useState, useCallback } from 'react'
import { KEYPOINT_COLORS } from '../../constants/colors'
import { predictKeypoints, convertPredictionToKeypoints } from '../../services/autoLabel'
import { ImageData, Keypoint, Project, COCOAnnotation, KeypointVisibility } from '../../types'
import {
  cocoFormatToKeypoints,
  createCOCOAnnotation,
  keypointsToCOCOFormat,
} from '../../utils/cocoFormat'

interface AnnotationCanvasProps {
  image: ImageData
  project: Project
  annotations: COCOAnnotation[]
  currentKeypoint: number
  selectedAnnotationId: string | null
  onAnnotationSelect: (annotationId: string | null) => void
  onAnnotationAdd: (annotation: COCOAnnotation) => void
  onAnnotationUpdate: (annotationId: string, annotation: COCOAnnotation) => void
  onAnnotationDelete: (annotationId: string) => void
  onKeypointUpdate: (annotationId: string, keypointIndex: number, keypoint: Keypoint) => void
  viewSettings?: {
    scale?: number
    offset?: { x: number; y: number }
    keypointRadius?: number
    rectangleWidth?: number
  }
  onViewSettingsChange?: (settings: {
    scale?: number
    offset?: { x: number; y: number }
    showGrid?: boolean
    autoAIEnabled?: boolean
    keypointRadius?: number
    rectangleWidth?: number
    showOnlySelected?: boolean
    currentTool?: 'select' | 'pan'
  }) => void
  keypointRadius?: number
  rectangleWidth?: number
  isFullscreen?: boolean
  onToggleFullscreen?: () => void
  shouldAutoFit?: boolean
  onAutoFitComplete?: () => void
  readOnly?: boolean
  annotationColors?: Record<string, string>
}

type Tool = 'select' | 'pan'

export default function AnnotationCanvas({
  image,
  project,
  annotations,
  currentKeypoint,
  selectedAnnotationId,
  onAnnotationSelect,
  onAnnotationAdd,
  onAnnotationUpdate,
  onAnnotationDelete,
  onKeypointUpdate,
  viewSettings,
  onViewSettingsChange,
  keypointRadius = 5,
  rectangleWidth = 3,
  isFullscreen = false,
  onToggleFullscreen,
  shouldAutoFit = false,
  onAutoFitComplete,
  readOnly = false,
  annotationColors,
}: AnnotationCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(viewSettings?.scale || 0.5)
  const [offset, setOffset] = useState(viewSettings?.offset || { x: 60, y: 60 })
  const [isDragging, setIsDragging] = useState(false)
  const [draggedKeypointIndex, setDraggedKeypointIndex] = useState<number | null>(null)
  const [imageLoaded, setImageLoaded] = useState(false)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null)
  const [pixelColor, setPixelColor] = useState<string>('')
  const [canvasMousePos, setCanvasMousePos] = useState<{ x: number; y: number } | null>(null)
  const [currentTool, setCurrentTool] = useState<Tool>(() => {
    if (readOnly) return 'pan'
    const saved = localStorage.getItem('annotation-currentTool')
    return (saved as Tool) || 'select'
  })
  const [isPanning, setIsPanning] = useState(false)
  const [panStart, setPanStart] = useState({ x: 0, y: 0 })
  const [showGrid, setShowGrid] = useState(() => {
    const saved = localStorage.getItem('annotation-showGrid')
    return saved === 'true'
  })
  const [showOnlySelected, setShowOnlySelected] = useState(() => {
    const saved = localStorage.getItem('annotation-showOnlySelected')
    return saved === 'true'
  })

  // Bounding box drawing state
  const [isDrawingBox, setIsDrawingBox] = useState(false)
  const [boxStart, setBoxStart] = useState<{ x: number; y: number } | null>(null)
  const [tempBox, setTempBox] = useState<[number, number, number, number] | null>(null)
  const [hoverKeypointIndex, setHoverKeypointIndex] = useState<number | null>(null)

  // Bounding box resize state
  const [isResizingBox, setIsResizingBox] = useState(false)
  const [resizeCorner, setResizeCorner] = useState<'tl' | 'tr' | 'bl' | 'br' | null>(null)
  const [resizeStart, setResizeStart] = useState<{ x: number; y: number } | null>(null)
  const [originalBbox, setOriginalBbox] = useState<[number, number, number, number] | null>(null)
  const [isAutoLabeling, setIsAutoLabeling] = useState(false)
  const isAutoLabelFeatureEnabled = import.meta.env.VITE_ENABLE_AUTO_LABEL === 'true'

  const [autoAIEnabled, setAutoAIEnabled] = useState(() => {
    if (!isAutoLabelFeatureEnabled) return false
    const saved = localStorage.getItem('annotation-autoAIEnabled')
    return saved === 'true'
  })
  const pendingAutoLabelRef = useRef<string | null>(null)
  const spaceKeyPressedRef = useRef(false)
  const toolBeforeSpaceRef = useRef<Tool | null>(null)

  // Grid and axis settings
  const AXIS_PADDING = 40
  const GRID_COLOR = '#e0e0e0'
  const AXIS_COLOR = '#333333'

  // Load image
  useEffect(() => {
    setImageLoaded(false)
    const imageUrl = image.url || ''

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      imageRef.current = img
      setImageLoaded(true)
    }
    img.onerror = () => {
      const fallbackImg = new Image()
      fallbackImg.crossOrigin = 'anonymous' // Add crossOrigin to fallback too
      fallbackImg.onload = () => {
        imageRef.current = fallbackImg
        setImageLoaded(true)
      }
      fallbackImg.src = imageUrl
    }
    img.src = imageUrl
  }, [image])

  // Initialize view settings from props (only for image-specific settings)
  useEffect(() => {
    if (viewSettings) {
      if (viewSettings.scale !== undefined) setScale(viewSettings.scale)
      if (viewSettings.offset !== undefined) setOffset(viewSettings.offset)
      // Don't notify parent about keypointRadius and rectangleWidth changes
      // The parent manages these values directly
    }
  }, [viewSettings])

  // Force pan tool when readOnly
  useEffect(() => {
    if (readOnly) {
      setCurrentTool('pan')
    }
  }, [readOnly])

  // Define fitToView function before using it
  const fitToView = useCallback(() => {
    if (!imageRef.current || !containerRef.current) return

    const padding = 20
    const canvasHeight = containerRef.current.clientHeight - AXIS_PADDING - padding * 2

    // Scale based only on height to fit the canvas height exactly
    const newScale = canvasHeight / imageRef.current.height

    // Align to top-left corner with padding
    const newOffset = {
      x: AXIS_PADDING + padding,
      y: AXIS_PADDING + padding,
    }

    setScale(newScale)
    setOffset(newOffset)

    if (onViewSettingsChange) {
      onViewSettingsChange({
        scale: newScale,
        offset: newOffset,
        showGrid,
        autoAIEnabled,
        showOnlySelected,
        currentTool,
      })
    }
  }, [onViewSettingsChange, showGrid, autoAIEnabled, showOnlySelected, currentTool])

  // Auto-fit to screen when requested
  useEffect(() => {
    if (shouldAutoFit && imageLoaded) {
      // Reuse existing fitToView function
      fitToView()

      // Notify parent that auto-fit is complete
      if (onAutoFitComplete) {
        onAutoFitComplete()
      }
    }
  }, [shouldAutoFit, imageLoaded, fitToView, onAutoFitComplete])

  // Main drawing function
  const draw = useCallback(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx || !imageLoaded || !imageRef.current) return

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Fill background
    ctx.fillStyle = '#f3f4f6'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // Save context for clipping
    ctx.save()

    // Create clipping region for image area
    ctx.beginPath()
    ctx.rect(AXIS_PADDING, AXIS_PADDING, canvas.width - AXIS_PADDING, canvas.height - AXIS_PADDING)
    ctx.clip()

    // Apply transformations
    ctx.translate(offset.x, offset.y)
    ctx.scale(scale, scale)

    // Draw image
    ctx.drawImage(imageRef.current, 0, 0)

    // Draw annotations
    annotations.forEach(annotation => {
      const isSelected = annotation.id === selectedAnnotationId
      const bbox = annotation.bbox
      const keypoints = cocoFormatToKeypoints(annotation.keypoints)
      const customColor = annotationColors?.[annotation.id]

      // Draw bounding box
      ctx.strokeStyle = isSelected ? '#FF00FF' : customColor || '#8B008B'
      ctx.lineWidth = (isSelected ? rectangleWidth + 1 : rectangleWidth) / scale
      ctx.strokeRect(bbox[0], bbox[1], bbox[2], bbox[3])

      // Draw corner handles for selected annotation
      if (isSelected) {
        const cornerSize = 6 / scale
        ctx.fillStyle = '#FF00FF'
        ctx.strokeStyle = '#FFFFFF'
        ctx.lineWidth = 1 / scale

        // Top-left corner
        ctx.fillRect(bbox[0] - cornerSize / 2, bbox[1] - cornerSize / 2, cornerSize, cornerSize)
        ctx.strokeRect(bbox[0] - cornerSize / 2, bbox[1] - cornerSize / 2, cornerSize, cornerSize)

        // Top-right corner
        ctx.fillRect(
          bbox[0] + bbox[2] - cornerSize / 2,
          bbox[1] - cornerSize / 2,
          cornerSize,
          cornerSize
        )
        ctx.strokeRect(
          bbox[0] + bbox[2] - cornerSize / 2,
          bbox[1] - cornerSize / 2,
          cornerSize,
          cornerSize
        )

        // Bottom-left corner
        ctx.fillRect(
          bbox[0] - cornerSize / 2,
          bbox[1] + bbox[3] - cornerSize / 2,
          cornerSize,
          cornerSize
        )
        ctx.strokeRect(
          bbox[0] - cornerSize / 2,
          bbox[1] + bbox[3] - cornerSize / 2,
          cornerSize,
          cornerSize
        )

        // Bottom-right corner
        ctx.fillRect(
          bbox[0] + bbox[2] - cornerSize / 2,
          bbox[1] + bbox[3] - cornerSize / 2,
          cornerSize,
          cornerSize
        )
        ctx.strokeRect(
          bbox[0] + bbox[2] - cornerSize / 2,
          bbox[1] + bbox[3] - cornerSize / 2,
          cornerSize,
          cornerSize
        )
      }

      // Decide whether to show keypoints/skeleton
      const shouldShowKeypoints = !showOnlySelected || isSelected

      // Draw skeleton connections
      if (shouldShowKeypoints) {
        ctx.strokeStyle = isSelected
          ? '#00FF00'
          : customColor
            ? customColor + '99'
            : 'rgba(0, 255, 0, 0.6)'
        ctx.lineWidth = 2 / scale
        project.skeleton_schema.forEach(([start, end]) => {
          const startKp = keypoints[start - 1]
          const endKp = keypoints[end - 1]
          if (startKp && endKp && startKp.x >= 0 && endKp.x >= 0) {
            ctx.beginPath()
            ctx.moveTo(startKp.x, startKp.y)
            ctx.lineTo(endKp.x, endKp.y)
            ctx.stroke()
          }
        })
      }

      // Draw keypoints
      if (shouldShowKeypoints) {
        keypoints.forEach((kp, index) => {
          if (kp.x < 0 || kp.y < 0) return

          const isCurrentKeypoint = isSelected && index === currentKeypoint
          const isHovered = isSelected && index === hoverKeypointIndex
          const isInferred = kp.visibility === 'inferred'

          // Use more muted colors for non-selected annotations
          if (isSelected) {
            ctx.fillStyle = KEYPOINT_COLORS[index % KEYPOINT_COLORS.length]
          } else if (customColor) {
            ctx.fillStyle = customColor + '99'
          } else {
            // Create a more muted version of the color
            const color = KEYPOINT_COLORS[index % KEYPOINT_COLORS.length]
            ctx.fillStyle = color + '99' // Add 60% opacity
          }

          ctx.strokeStyle = isCurrentKeypoint
            ? '#FFFFFF'
            : isSelected
              ? '#000000'
              : 'rgba(0, 0, 0, 0.6)'
          ctx.lineWidth = (isCurrentKeypoint ? 3 : isSelected ? 2 : 1) / scale

          const radius = isHovered ? keypointRadius * 1.2 : keypointRadius

          // Draw different style for inferred keypoints
          if (isInferred) {
            // Draw as a square for inferred keypoints
            const size = radius / scale
            ctx.fillRect(kp.x - size, kp.y - size, size * 2, size * 2)
            ctx.strokeRect(kp.x - size, kp.y - size, size * 2, size * 2)
          } else {
            // Draw as circle for visible keypoints
            ctx.beginPath()
            ctx.arc(kp.x, kp.y, radius / scale, 0, 2 * Math.PI)
            ctx.fill()
            ctx.stroke()
          }

          // Draw label only for selected annotation
          if (isSelected) {
            ctx.fillStyle = '#FFFFFF'
            ctx.font = `${12 / scale}px Arial`
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'
            ctx.fillText(project.keypoint_schema[index] || index.toString(), kp.x, kp.y)
          }
        })
      }
    })

    // Draw temporary box while drawing
    if (tempBox) {
      ctx.strokeStyle = '#FF00FF'
      ctx.lineWidth = rectangleWidth / scale
      ctx.setLineDash([5 / scale, 5 / scale])
      ctx.strokeRect(tempBox[0], tempBox[1], tempBox[2], tempBox[3])
      ctx.setLineDash([])
    }

    // Restore context
    ctx.restore()

    // Draw axes and grid
    drawAxes(ctx)

    // Draw cursor info in select mode (draw on top of everything)
    if (
      currentTool === 'select' &&
      canvasMousePos &&
      mousePos &&
      canvasMousePos.x >= AXIS_PADDING &&
      canvasMousePos.y >= AXIS_PADDING
    ) {
      // Simple info display with color swatch
      const x = Math.round(mousePos.x)
      const y = Math.round(mousePos.y)

      // Position near cursor
      const infoX = canvasMousePos.x + 20
      const infoY = canvasMousePos.y - 20

      // Draw semi-transparent background
      ctx.save()

      // Simple text display
      ctx.font = '13px monospace'
      ctx.textAlign = 'left'
      ctx.textBaseline = 'middle' // Use middle for better vertical alignment

      // Measure text
      const coordText = `${x}, ${y}`
      let rgbText = ''
      let hasColor = false
      if (pixelColor) {
        // Extract RGB values and format as (r, g, b)
        const match = pixelColor.match(/rgb\((\d+),(\d+),(\d+)\)/)
        if (match) {
          rgbText = `(${match[1]}, ${match[2]}, ${match[3]})`
          hasColor = true
        }
      }

      // Measure text parts
      const coordMetrics = ctx.measureText(coordText)
      const separatorText = hasColor ? ' | ' : ''
      const separatorMetrics = ctx.measureText(separatorText)
      const rgbMetrics = ctx.measureText(rgbText)
      const textHeight = 16

      // Background dimensions
      const bgPadding = 6
      const colorSwatchSize = 12
      const colorSwatchPadding = 3
      const totalTextWidth =
        coordMetrics.width +
        (hasColor
          ? separatorMetrics.width + colorSwatchSize + colorSwatchPadding + rgbMetrics.width
          : 0)
      const bgWidth = totalTextWidth + bgPadding * 2
      const bgHeight = textHeight + bgPadding * 2

      // Adjust position if too close to edge
      let drawX = infoX
      let drawY = infoY

      if (drawX + bgWidth > canvasRef.current!.width - 10) {
        drawX = canvasMousePos.x - bgWidth - 20
      }
      if (drawY < 10) {
        drawY = canvasMousePos.y + 20
      }

      // Draw background
      ctx.fillStyle = 'rgba(0, 0, 0, 0.75)'
      ctx.fillRect(drawX, drawY, bgWidth, bgHeight)

      // Calculate vertical center
      const centerY = drawY + bgHeight / 2

      // Draw text and color swatch
      ctx.fillStyle = '#ffffff'
      let currentX = drawX + bgPadding

      // Draw coordinates
      ctx.fillText(coordText, currentX, centerY)
      currentX += coordMetrics.width

      if (hasColor) {
        // Draw separator
        ctx.fillText(separatorText, currentX, centerY)
        currentX += separatorMetrics.width

        // Draw color swatch
        const swatchY = centerY - colorSwatchSize / 1.7 // Center swatch vertically
        ctx.fillStyle = pixelColor
        ctx.fillRect(currentX, swatchY, colorSwatchSize, colorSwatchSize)

        // Swatch border
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)'
        ctx.lineWidth = 1
        ctx.strokeRect(currentX, swatchY, colorSwatchSize, colorSwatchSize)

        currentX += colorSwatchSize + colorSwatchPadding

        // Draw RGB text
        ctx.fillStyle = '#ffffff'
        ctx.fillText(rgbText, currentX, centerY)
      }

      ctx.restore()
    }
  }, [
    scale,
    offset,
    imageLoaded,
    project,
    annotations,
    currentKeypoint,
    selectedAnnotationId,
    tempBox,
    showGrid,
    keypointRadius,
    rectangleWidth,
    hoverKeypointIndex,
    currentTool,
    canvasMousePos,
    mousePos,
    pixelColor,
    showOnlySelected,
  ])

  // Draw axes and grid
  const drawAxes = (ctx: CanvasRenderingContext2D) => {
    if (!imageRef.current || !canvasRef.current) return

    const imgWidth = imageRef.current.width
    const imgHeight = imageRef.current.height

    // Calculate visible range
    const visibleLeft = Math.max(0, (AXIS_PADDING - offset.x) / scale)
    const visibleRight = Math.min(imgWidth, (canvasRef.current!.width - offset.x) / scale)
    const visibleTop = Math.max(0, (AXIS_PADDING - offset.y) / scale)
    const visibleBottom = Math.min(imgHeight, (canvasRef.current!.height - offset.y) / scale)

    // Calculate grid steps
    const xStep = Math.pow(10, Math.floor(Math.log10((visibleRight - visibleLeft) / 5)))
    const yStep = Math.pow(10, Math.floor(Math.log10((visibleBottom - visibleTop) / 5)))

    // Draw grid lines if enabled
    if (showGrid) {
      ctx.strokeStyle = GRID_COLOR
      ctx.lineWidth = 1

      // Vertical grid lines
      for (let x = Math.ceil(visibleLeft / xStep) * xStep; x < visibleRight; x += xStep) {
        const canvasX = x * scale + offset.x
        if (canvasX > AXIS_PADDING) {
          ctx.beginPath()
          ctx.moveTo(canvasX, AXIS_PADDING)
          ctx.lineTo(canvasX, canvasRef.current!.height)
          ctx.stroke()
        }
      }

      // Horizontal grid lines
      for (let y = Math.ceil(visibleTop / yStep) * yStep; y < visibleBottom; y += yStep) {
        const canvasY = y * scale + offset.y
        if (canvasY > AXIS_PADDING) {
          ctx.beginPath()
          ctx.moveTo(AXIS_PADDING, canvasY)
          ctx.lineTo(canvasRef.current!.width, canvasY)
          ctx.stroke()
        }
      }
    }

    // Draw axis lines
    ctx.strokeStyle = AXIS_COLOR
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(AXIS_PADDING, AXIS_PADDING)
    ctx.lineTo(AXIS_PADDING, canvasRef.current!.height)
    ctx.moveTo(AXIS_PADDING, AXIS_PADDING)
    ctx.lineTo(canvasRef.current!.width, AXIS_PADDING)
    ctx.stroke()

    // Draw axis labels
    ctx.fillStyle = AXIS_COLOR
    ctx.font = '12px Arial'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    // X-axis labels
    for (let x = Math.ceil(visibleLeft / xStep) * xStep; x < visibleRight; x += xStep) {
      const canvasX = x * scale + offset.x
      if (canvasX > AXIS_PADDING) {
        ctx.fillText(x.toString(), canvasX, AXIS_PADDING / 2)
      }
    }

    // Y-axis labels
    ctx.textAlign = 'right'
    for (let y = Math.ceil(visibleTop / yStep) * yStep; y < visibleBottom; y += yStep) {
      const canvasY = y * scale + offset.y
      if (canvasY > AXIS_PADDING) {
        ctx.fillText(y.toString(), AXIS_PADDING - 5, canvasY)
      }
    }
  }

  // Redraw when dependencies change
  useEffect(() => {
    draw()
  }, [draw])

  // Update canvas size
  useEffect(() => {
    const updateCanvasSize = () => {
      if (!canvasRef.current || !containerRef.current) return
      canvasRef.current.width = containerRef.current.clientWidth
      canvasRef.current.height = containerRef.current.clientHeight
      draw()
    }

    updateCanvasSize()
    window.addEventListener('resize', updateCanvasSize)
    return () => window.removeEventListener('resize', updateCanvasSize)
  }, [draw])

  // Add wheel event listener with passive: false
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const handleWheelEvent = (e: WheelEvent) => {
      // Only allow zoom in pan mode
      if (currentTool !== 'pan') return

      e.preventDefault()

      const rect = canvas.getBoundingClientRect()
      const canvasX = e.clientX - rect.left
      const canvasY = e.clientY - rect.top

      if (canvasX < AXIS_PADDING || canvasY < AXIS_PADDING) return

      const imagePos = {
        x: (canvasX - offset.x) / scale,
        y: (canvasY - offset.y) / scale,
      }

      const scaleDelta = e.deltaY > 0 ? 0.9 : 1.1
      const newScale = Math.max(0.1, Math.min(5, scale * scaleDelta))

      // Adjust offset to zoom towards mouse position
      const newOffset = {
        x: canvasX - imagePos.x * newScale,
        y: canvasY - imagePos.y * newScale,
      }

      setScale(newScale)
      setOffset(newOffset)

      if (onViewSettingsChange) {
        onViewSettingsChange({
          scale: newScale,
          offset: newOffset,
          showGrid,
          autoAIEnabled,
          keypointRadius,
          rectangleWidth,
          showOnlySelected,
          currentTool,
        })
      }
    }

    canvas.addEventListener('wheel', handleWheelEvent, { passive: false })
    return () => canvas.removeEventListener('wheel', handleWheelEvent)
  }, [
    currentTool,
    scale,
    offset,
    showGrid,
    autoAIEnabled,
    keypointRadius,
    rectangleWidth,
    showOnlySelected,
    onViewSettingsChange,
  ])

  // Convert canvas coordinates to image coordinates
  const canvasToImage = (canvasX: number, canvasY: number) => {
    return {
      x: (canvasX - offset.x) / scale,
      y: (canvasY - offset.y) / scale,
    }
  }

  // Check if a point is near a corner of the bounding box
  const findCornerAt = (
    x: number,
    y: number
  ): { corner: 'tl' | 'tr' | 'bl' | 'br'; annotationId: string } | null => {
    if (!selectedAnnotationId) return null

    const annotation = annotations.find(a => a.id === selectedAnnotationId)
    if (!annotation) return null

    const bbox = annotation.bbox
    const cornerSize = 8 / scale // Corner handle size in image coordinates

    // Top-left corner
    if (Math.abs(x - bbox[0]) < cornerSize && Math.abs(y - bbox[1]) < cornerSize) {
      return { corner: 'tl', annotationId: selectedAnnotationId }
    }

    // Top-right corner
    if (Math.abs(x - (bbox[0] + bbox[2])) < cornerSize && Math.abs(y - bbox[1]) < cornerSize) {
      return { corner: 'tr', annotationId: selectedAnnotationId }
    }

    // Bottom-left corner
    if (Math.abs(x - bbox[0]) < cornerSize && Math.abs(y - (bbox[1] + bbox[3])) < cornerSize) {
      return { corner: 'bl', annotationId: selectedAnnotationId }
    }

    // Bottom-right corner
    if (
      Math.abs(x - (bbox[0] + bbox[2])) < cornerSize &&
      Math.abs(y - (bbox[1] + bbox[3])) < cornerSize
    ) {
      return { corner: 'br', annotationId: selectedAnnotationId }
    }

    return null
  }

  // Find annotation at position
  const findAnnotationAt = (x: number, y: number): string | null => {
    for (let i = annotations.length - 1; i >= 0; i--) {
      const ann = annotations[i]
      const bbox = ann.bbox
      if (x >= bbox[0] && x <= bbox[0] + bbox[2] && y >= bbox[1] && y <= bbox[1] + bbox[3]) {
        return ann.id
      }
    }
    return null
  }

  // Find keypoint at position
  const findKeypointAt = (
    x: number,
    y: number
  ): { annotationId: string; keypointIndex: number } | null => {
    if (!selectedAnnotationId) return null

    const annotation = annotations.find(a => a.id === selectedAnnotationId)
    if (!annotation) return null

    const keypoints = cocoFormatToKeypoints(annotation.keypoints)
    const threshold = 10 / scale

    for (let i = 0; i < keypoints.length; i++) {
      const kp = keypoints[i]
      if (kp.x < 0 || kp.y < 0) continue
      const distance = Math.sqrt(Math.pow(x - kp.x, 2) + Math.pow(y - kp.y, 2))
      if (distance < threshold) {
        return { annotationId: annotation.id, keypointIndex: i }
      }
    }
    return null
  }

  // Zoom functions
  const zoomIn = () => {
    const newScale = Math.min(scale * 1.2, 5)
    const center = {
      x: containerRef.current!.clientWidth / 2,
      y: containerRef.current!.clientHeight / 2,
    }

    const newOffset = {
      x: center.x - (center.x - offset.x) * (newScale / scale),
      y: center.y - (center.y - offset.y) * (newScale / scale),
    }

    setScale(newScale)
    setOffset(newOffset)

    if (onViewSettingsChange) {
      onViewSettingsChange({
        scale: newScale,
        offset: newOffset,
        showGrid,
        autoAIEnabled,
        showOnlySelected,
        currentTool,
      })
    }
  }

  const zoomOut = () => {
    const newScale = Math.max(scale * 0.8, 0.1)
    const center = {
      x: containerRef.current!.clientWidth / 2,
      y: containerRef.current!.clientHeight / 2,
    }

    const newOffset = {
      x: center.x - (center.x - offset.x) * (newScale / scale),
      y: center.y - (center.y - offset.y) * (newScale / scale),
    }

    setScale(newScale)
    setOffset(newOffset)

    if (onViewSettingsChange) {
      onViewSettingsChange({
        scale: newScale,
        offset: newOffset,
        showGrid,
        autoAIEnabled,
        showOnlySelected,
        currentTool,
      })
    }
  }

  // Fit to selected bounding box
  const fitToBoundingBox = () => {
    if (!selectedAnnotationId || !containerRef.current) return

    const annotation = annotations.find(a => a.id === selectedAnnotationId)
    if (!annotation) return

    const bbox = annotation.bbox
    const padding = 20

    // Calculate scale to fit bounding box nicely in view
    const canvasWidth = containerRef.current.clientWidth - AXIS_PADDING - padding * 2
    const canvasHeight = containerRef.current.clientHeight - AXIS_PADDING - padding * 2

    const scaleX = canvasWidth / bbox[2]
    const scaleY = canvasHeight / bbox[3]
    const newScale = Math.min(scaleX, scaleY, 2)

    // Align bounding box to top-left corner with padding
    const newOffset = {
      x: AXIS_PADDING + padding - bbox[0] * newScale,
      y: AXIS_PADDING + padding - bbox[1] * newScale,
    }

    setScale(newScale)
    setOffset(newOffset)

    if (onViewSettingsChange) {
      onViewSettingsChange({
        scale: newScale,
        offset: newOffset,
        showGrid,
        autoAIEnabled,
        showOnlySelected,
        currentTool,
      })
    }
  }

  const toggleGrid = () => {
    const newShowGrid = !showGrid
    setShowGrid(newShowGrid)

    if (onViewSettingsChange) {
      onViewSettingsChange({
        scale,
        offset,
        showGrid: newShowGrid,
        autoAIEnabled,
        keypointRadius,
        rectangleWidth,
        showOnlySelected,
        currentTool,
      })
    }
  }

  // Download current canvas view (only image area)
  const downloadImage = () => {
    if (!canvasRef.current || !imageRef.current) return

    // Calculate visible image area
    const visibleLeft = Math.max(0, -offset.x / scale)
    const visibleTop = Math.max(0, -offset.y / scale)
    const visibleRight = Math.min(
      imageRef.current.width,
      (canvasRef.current.width - offset.x) / scale
    )
    const visibleBottom = Math.min(
      imageRef.current.height,
      (canvasRef.current.height - offset.y) / scale
    )

    const visibleWidth = visibleRight - visibleLeft
    const visibleHeight = visibleBottom - visibleTop

    if (visibleWidth <= 0 || visibleHeight <= 0) return

    // Create a new canvas for the download
    const downloadCanvas = document.createElement('canvas')
    const downloadCtx = downloadCanvas.getContext('2d')
    if (!downloadCtx) return

    // Set canvas size to match visible area at current scale
    downloadCanvas.width = visibleWidth * scale
    downloadCanvas.height = visibleHeight * scale

    // Try direct approach first, then fallback if CORS issue
    const tryDirectDownload = () => {
      const downloadImg = new Image()
      downloadImg.crossOrigin = 'anonymous'

      downloadImg.onerror = e => {
        console.error('Direct download failed, trying fallback:', e)
        // Fallback: Use fetch to get image as blob
        fetch(image.url || '')
          .then(response => response.blob())
          .then(blob => {
            const url = URL.createObjectURL(blob)
            const fallbackImg = new Image()

            fallbackImg.onload = () => {
              drawAndDownload(fallbackImg)
              URL.revokeObjectURL(url)
            }

            fallbackImg.onerror = () => {
              console.error('Fallback also failed')
              alert('Unable to download image. Please try saving from the browser.')
            }

            fallbackImg.src = url
          })
          .catch(err => {
            console.error('Fetch failed:', err)
            alert('Failed to download image. Please try again.')
          })
      }

      downloadImg.onload = () => {
        drawAndDownload(downloadImg)
      }

      downloadImg.src = image.url || ''
    }

    const drawAndDownload = (img: HTMLImageElement) => {
      // Draw the visible portion of the image
      downloadCtx.drawImage(
        img,
        visibleLeft,
        visibleTop,
        visibleWidth,
        visibleHeight,
        0,
        0,
        downloadCanvas.width,
        downloadCanvas.height
      )

      // Draw annotations at correct scale
      downloadCtx.save()
      downloadCtx.scale(scale, scale)
      downloadCtx.translate(-visibleLeft, -visibleTop)

      // Draw annotations (same logic as main draw function)
      annotations.forEach(annotation => {
        const isSelected = annotation.id === selectedAnnotationId
        const bbox = annotation.bbox
        const keypoints = cocoFormatToKeypoints(annotation.keypoints)
        const customColor = annotationColors?.[annotation.id]

        // Draw bounding box
        downloadCtx.strokeStyle = isSelected ? '#FF00FF' : customColor || '#8B008B'
        downloadCtx.lineWidth = (isSelected ? rectangleWidth + 1 : rectangleWidth) / scale
        downloadCtx.strokeRect(bbox[0], bbox[1], bbox[2], bbox[3])

        // Decide whether to show keypoints/skeleton
        const shouldShowKeypoints = !showOnlySelected || isSelected

        // Draw skeleton connections
        if (shouldShowKeypoints) {
          downloadCtx.strokeStyle = isSelected
            ? '#00FF00'
            : customColor
              ? customColor + '99'
              : 'rgba(0, 255, 0, 0.6)'
          downloadCtx.lineWidth = 2 / scale
          project.skeleton_schema.forEach(([start, end]) => {
            const startKp = keypoints[start - 1]
            const endKp = keypoints[end - 1]
            if (startKp && endKp && startKp.x >= 0 && endKp.x >= 0) {
              downloadCtx.beginPath()
              downloadCtx.moveTo(startKp.x, startKp.y)
              downloadCtx.lineTo(endKp.x, endKp.y)
              downloadCtx.stroke()
            }
          })
        }

        // Draw keypoints
        if (shouldShowKeypoints) {
          keypoints.forEach((kp, index) => {
            if (kp.x < 0 || kp.y < 0) return

            const isCurrentKeypoint = isSelected && index === currentKeypoint
            const isHovered = isSelected && index === hoverKeypointIndex

            // Use more muted colors for non-selected annotations
            if (isSelected) {
              downloadCtx.fillStyle = KEYPOINT_COLORS[index % KEYPOINT_COLORS.length]
            } else if (customColor) {
              downloadCtx.fillStyle = customColor + '99'
            } else {
              const color = KEYPOINT_COLORS[index % KEYPOINT_COLORS.length]
              downloadCtx.fillStyle = color + '99' // Add 60% opacity
            }

            downloadCtx.strokeStyle = isCurrentKeypoint
              ? '#FFFFFF'
              : isSelected
                ? '#000000'
                : 'rgba(0, 0, 0, 0.6)'
            downloadCtx.lineWidth = (isCurrentKeypoint ? 3 : isSelected ? 2 : 1) / scale

            const radius = isHovered ? keypointRadius * 1.2 : keypointRadius

            downloadCtx.beginPath()
            downloadCtx.arc(kp.x, kp.y, radius / scale, 0, 2 * Math.PI)
            downloadCtx.fill()
            downloadCtx.stroke()

            // Draw label only for selected annotation
            if (isSelected) {
              downloadCtx.fillStyle = '#FFFFFF'
              downloadCtx.font = `${12 / scale}px Arial`
              downloadCtx.textAlign = 'center'
              downloadCtx.textBaseline = 'middle'
              downloadCtx.fillText(project.keypoint_schema[index] || index.toString(), kp.x, kp.y)
            }
          })
        }
      })

      downloadCtx.restore()

      // Download
      try {
        downloadCanvas.toBlob(blob => {
          if (!blob) {
            console.error('Failed to create blob')
            alert('Failed to download image. Please try again.')
            return
          }
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          // Generate filename with timestamp
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
          a.download = `${image.filename.split('.')[0]}_${timestamp}.png`
          a.click()
          URL.revokeObjectURL(url)
        })
      } catch (error) {
        console.error('Download error:', error)
        if (error instanceof Error && error.message.includes('tainted')) {
          alert(
            'Unable to download image due to browser security restrictions. The image may be from a different domain.'
          )
        } else {
          alert('Failed to download image. Please try again.')
        }
      }
    }

    // Start the download process
    tryDirectDownload()
  }

  // Auto-label function for a specific annotation
  const autoLabelAnnotation = useCallback(
    async (annotationId: string) => {
      const annotation = annotations.find(a => a.id === annotationId)
      if (!annotation) {
        return
      }

      try {
        setIsAutoLabeling(true)

        // Get image URL with token
        const imageUrl = image.url || ''

        // Call prediction API with the bounding box
        const prediction = await predictKeypoints(imageUrl, annotation.bbox)

        // Convert prediction to keypoints (with bbox offset)
        const predictedKeypoints = convertPredictionToKeypoints(
          prediction,
          project.keypoint_schema.length,
          annotation.bbox
        )

        // Update the annotation with predicted keypoints
        const updatedAnnotation = {
          ...annotation,
          keypoints: keypointsToCOCOFormat(predictedKeypoints),
          num_keypoints: predictedKeypoints.filter(kp => kp.x >= 0 && kp.y >= 0).length,
        }

        onAnnotationUpdate(annotationId, updatedAnnotation)
      } catch (error) {
        console.error('Auto-label error:', error)
        // Only show alert if manually triggered (not auto)
        if (annotationId === selectedAnnotationId) {
          alert('Failed to auto-label. Please try again.')
        }
      } finally {
        setIsAutoLabeling(false)
      }
    },
    [
      annotations,
      selectedAnnotationId,
      image.storage_path,
      project.keypoint_schema.length,
      onAnnotationUpdate,
    ]
  )

  // Auto-label function for manual button
  const handleAutoLabel = async () => {
    if (!selectedAnnotationId || isAutoLabeling) return
    await autoLabelAnnotation(selectedAnnotationId)
  }

  // Handle pending auto-label
  useEffect(() => {
    if (
      pendingAutoLabelRef.current &&
      annotations.find(a => a.id === pendingAutoLabelRef.current)
    ) {
      const annotationId = pendingAutoLabelRef.current
      pendingAutoLabelRef.current = null
      autoLabelAnnotation(annotationId)
    }
  }, [annotations, autoLabelAnnotation])

  // Mouse event handlers
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current?.getBoundingClientRect()
    if (!rect) return

    const canvasX = e.clientX - rect.left
    const canvasY = e.clientY - rect.top

    if (canvasX < AXIS_PADDING || canvasY < AXIS_PADDING) return

    const imagePos = canvasToImage(canvasX, canvasY)

    if (currentTool === 'pan') {
      setIsPanning(true)
      setPanStart({ x: e.clientX - offset.x, y: e.clientY - offset.y })
      return
    }

    if (readOnly) return

    // Right click
    if (e.button === 2) {
      e.preventDefault()

      // Shift + right click to delete bounding box
      if (e.shiftKey) {
        const annotationId = findAnnotationAt(imagePos.x, imagePos.y)
        if (annotationId) {
          onAnnotationDelete(annotationId)
        }
      } else {
        // Regular right click to delete keypoint
        const keypointResult = findKeypointAt(imagePos.x, imagePos.y)
        if (keypointResult) {
          onKeypointUpdate(keypointResult.annotationId, keypointResult.keypointIndex, {
            x: -1,
            y: -1,
            confidence: 1.0,
            visibility: 'not_labeled',
          })
        }
      }
      return
    }

    // Shift + left click to draw bounding box
    if (e.shiftKey && e.button === 0) {
      setIsDrawingBox(true)
      setBoxStart(imagePos)
      setTempBox(null)
      return
    }

    // Left click
    if (e.button === 0) {
      // First check if clicking on a corner handle (highest priority)
      const cornerResult = findCornerAt(imagePos.x, imagePos.y)
      if (cornerResult) {
        setIsResizingBox(true)
        setResizeCorner(cornerResult.corner)
        setResizeStart(imagePos)
        const annotation = annotations.find(a => a.id === cornerResult.annotationId)
        if (annotation) {
          setOriginalBbox([...annotation.bbox])
        }
        return
      }

      // Then check if clicking on a keypoint (high priority)
      const keypointResult = findKeypointAt(imagePos.x, imagePos.y)

      if (keypointResult) {
        // Ctrl+click to cycle visibility state
        if (e.ctrlKey || e.metaKey) {
          const annotation = annotations.find(a => a.id === keypointResult.annotationId)
          if (annotation) {
            const keypoints = cocoFormatToKeypoints(annotation.keypoints)
            const currentKp = keypoints[keypointResult.keypointIndex]

            // Cycle through visibility states: visible -> inferred -> visible
            const newVisibility = currentKp.visibility === 'visible' ? 'inferred' : 'visible'

            onKeypointUpdate(keypointResult.annotationId, keypointResult.keypointIndex, {
              ...currentKp,
              visibility: newVisibility,
            })
          }
        } else {
          // Regular click to drag
          setIsDragging(true)
          setDraggedKeypointIndex(keypointResult.keypointIndex)
        }
      } else {
        // Check if clicking on annotation
        const annotationId = findAnnotationAt(imagePos.x, imagePos.y)

        if (annotationId) {
          // Only notify selection change if different
          if (annotationId !== selectedAnnotationId) {
            onAnnotationSelect(annotationId)
          }

          // If clicking inside already-selected annotation, add keypoint
          if (annotationId === selectedAnnotationId) {
            const annotation = annotations.find(a => a.id === selectedAnnotationId)
            if (annotation) {
              const bbox = annotation.bbox
              if (
                imagePos.x >= bbox[0] &&
                imagePos.x <= bbox[0] + bbox[2] &&
                imagePos.y >= bbox[1] &&
                imagePos.y <= bbox[1] + bbox[3]
              ) {
                onKeypointUpdate(selectedAnnotationId, currentKeypoint, {
                  x: imagePos.x,
                  y: imagePos.y,
                  confidence: 1.0,
                  visibility: 'visible',
                })
              }
            }
          }
        } else if (selectedAnnotationId) {
          // Clicking outside - check if inside selected bbox
          const annotation = annotations.find(a => a.id === selectedAnnotationId)
          if (annotation) {
            const bbox = annotation.bbox
            if (
              imagePos.x >= bbox[0] &&
              imagePos.x <= bbox[0] + bbox[2] &&
              imagePos.y >= bbox[1] &&
              imagePos.y <= bbox[1] + bbox[3]
            ) {
              onKeypointUpdate(selectedAnnotationId, currentKeypoint, {
                x: imagePos.x,
                y: imagePos.y,
                confidence: 1.0,
                visibility: 'visible',
              })
            }
          }
        }
      }
    }
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current?.getBoundingClientRect()
    if (!rect) return

    const canvasX = e.clientX - rect.left
    const canvasY = e.clientY - rect.top

    // Update canvas mouse position for cursor display
    setCanvasMousePos({ x: canvasX, y: canvasY })

    // Update mouse position and pixel color
    if (canvasX >= AXIS_PADDING && canvasY >= AXIS_PADDING) {
      const imagePos = canvasToImage(canvasX, canvasY)
      setMousePos(imagePos)

      // Get pixel color
      if (
        imageRef.current &&
        imageLoaded &&
        imagePos.x >= 0 &&
        imagePos.x < imageRef.current.width &&
        imagePos.y >= 0 &&
        imagePos.y < imageRef.current.height
      ) {
        try {
          const tempCanvas = document.createElement('canvas')
          const tempCtx = tempCanvas.getContext('2d')
          if (tempCtx) {
            tempCanvas.width = 1
            tempCanvas.height = 1
            tempCtx.drawImage(imageRef.current, imagePos.x, imagePos.y, 1, 1, 0, 0, 1, 1)
            const pixelData = tempCtx.getImageData(0, 0, 1, 1).data
            const r = pixelData[0]
            const g = pixelData[1]
            const b = pixelData[2]
            setPixelColor(`rgb(${r},${g},${b})`)
          }
        } catch (err) {
          // Cross-origin access denied, skip pixel color reading
          setPixelColor('')
        }
      } else {
        setPixelColor('')
      }
    } else {
      setMousePos(null)
      setPixelColor('')
    }

    if (isPanning) {
      setOffset({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      })
      return
    }

    if (canvasX < AXIS_PADDING || canvasY < AXIS_PADDING) {
      setHoverKeypointIndex(null)
      return
    }

    const imagePos = canvasToImage(canvasX, canvasY)

    // Check if hovering over a keypoint
    if (!isDragging && !isDrawingBox && !isResizingBox) {
      const hoveredKeypoint = findKeypointAt(imagePos.x, imagePos.y)
      setHoverKeypointIndex(hoveredKeypoint ? hoveredKeypoint.keypointIndex : null)

      // Update cursor based on what's hovered
      if (canvasRef.current) {
        const corner = findCornerAt(imagePos.x, imagePos.y)
        if (corner) {
          // Set appropriate resize cursor based on corner
          switch (corner.corner) {
            case 'tl':
              canvasRef.current.style.cursor = 'nw-resize'
              break
            case 'tr':
              canvasRef.current.style.cursor = 'ne-resize'
              break
            case 'bl':
              canvasRef.current.style.cursor = 'sw-resize'
              break
            case 'br':
              canvasRef.current.style.cursor = 'se-resize'
              break
          }
        } else if (hoveredKeypoint) {
          canvasRef.current.style.cursor = 'move'
        } else {
          canvasRef.current.style.cursor = currentTool === 'pan' ? 'grab' : 'crosshair'
        }
      }
    }

    if (isResizingBox && resizeStart && originalBbox && selectedAnnotationId) {
      // Handle corner resizing
      const dx = imagePos.x - resizeStart.x
      const dy = imagePos.y - resizeStart.y

      const newBbox: [number, number, number, number] = [...originalBbox]

      switch (resizeCorner) {
        case 'tl': // Top-left
          newBbox[0] = originalBbox[0] + dx
          newBbox[1] = originalBbox[1] + dy
          newBbox[2] = originalBbox[2] - dx
          newBbox[3] = originalBbox[3] - dy
          break
        case 'tr': // Top-right
          newBbox[1] = originalBbox[1] + dy
          newBbox[2] = originalBbox[2] + dx
          newBbox[3] = originalBbox[3] - dy
          break
        case 'bl': // Bottom-left
          newBbox[0] = originalBbox[0] + dx
          newBbox[2] = originalBbox[2] - dx
          newBbox[3] = originalBbox[3] + dy
          break
        case 'br': // Bottom-right
          newBbox[2] = originalBbox[2] + dx
          newBbox[3] = originalBbox[3] + dy
          break
      }

      // Ensure minimum size
      if (newBbox[2] > 10 && newBbox[3] > 10) {
        const annotation = annotations.find(a => a.id === selectedAnnotationId)
        if (annotation) {
          onAnnotationUpdate(selectedAnnotationId, {
            ...annotation,
            bbox: newBbox,
          })
        }
      }
    } else if (isDrawingBox && boxStart) {
      const x = Math.min(boxStart.x, imagePos.x)
      const y = Math.min(boxStart.y, imagePos.y)
      const width = Math.abs(imagePos.x - boxStart.x)
      const height = Math.abs(imagePos.y - boxStart.y)
      setTempBox([x, y, width, height])
    } else if (isDragging && draggedKeypointIndex !== null && selectedAnnotationId) {
      // Get current keypoint to preserve its visibility state
      const annotation = annotations.find(a => a.id === selectedAnnotationId)
      if (annotation) {
        const keypoints = cocoFormatToKeypoints(annotation.keypoints)
        const currentKp = keypoints[draggedKeypointIndex]

        onKeypointUpdate(selectedAnnotationId, draggedKeypointIndex, {
          x: imagePos.x,
          y: imagePos.y,
          confidence: currentKp?.confidence || 1.0,
          visibility: currentKp?.visibility || 'visible',
        })
      }
    }
  }

  const handleMouseUp = async () => {
    if (isDrawingBox && tempBox && tempBox[2] > 5 && tempBox[3] > 5) {
      // Create new annotation with empty keypoints
      const emptyKeypoints = Array(project.keypoint_schema.length)
        .fill(null)
        .map(() => ({
          x: -1,
          y: -1,
          confidence: 1.0,
          visibility: 'not_labeled' as KeypointVisibility,
        }))

      const newAnnotation = createCOCOAnnotation(tempBox, emptyKeypoints)
      onAnnotationAdd(newAnnotation)
      onAnnotationSelect(newAnnotation.id)

      // Auto-label if enabled
      if (autoAIEnabled) {
        pendingAutoLabelRef.current = newAnnotation.id
      }
    }

    setIsDragging(false)
    setDraggedKeypointIndex(null)
    setIsDrawingBox(false)
    setBoxStart(null)
    setTempBox(null)
    setIsPanning(false)
    setHoverKeypointIndex(null)
    setCanvasMousePos(null)
    setIsResizingBox(false)
    setResizeCorner(null)
    setResizeStart(null)
    setOriginalBbox(null)

    if (onViewSettingsChange && (isPanning || isDrawingBox)) {
      onViewSettingsChange({
        scale,
        offset,
        showGrid,
        autoAIEnabled,
        keypointRadius,
        rectangleWidth,
        showOnlySelected,
        currentTool,
      })
    }
  }

  // Prevent context menu on canvas
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault()
    }

    canvas.addEventListener('contextmenu', handleContextMenu)
    return () => canvas.removeEventListener('contextmenu', handleContextMenu)
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if the active element is an input or textarea
      const activeElement = document.activeElement
      const isInputActive =
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.getAttribute('contenteditable') === 'true'

      // Don't handle space key if typing in an input field
      if (isInputActive) {
        return
      }

      // Space to temporarily switch to pan tool (hold down)
      if (e.key === ' ' && !spaceKeyPressedRef.current) {
        e.preventDefault()
        e.stopPropagation()
        spaceKeyPressedRef.current = true

        // Only switch to pan if we're currently in select mode
        if (currentTool === 'select') {
          toolBeforeSpaceRef.current = currentTool
          setCurrentTool('pan')
          // Don't save this temporary tool change to settings
        }
      }
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      // Check if the active element is an input or textarea
      const activeElement = document.activeElement
      const isInputActive =
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.getAttribute('contenteditable') === 'true'

      // Don't handle space key if typing in an input field
      if (isInputActive) {
        return
      }

      // Release space to switch back to previous tool
      if (e.key === ' ') {
        e.preventDefault()
        e.stopPropagation()
        spaceKeyPressedRef.current = false

        // Switch back to the tool we had before pressing space
        if (toolBeforeSpaceRef.current) {
          setCurrentTool(toolBeforeSpaceRef.current)
          toolBeforeSpaceRef.current = null
          // Don't save this temporary tool change to settings
        }
      }
    }

    // Reset space key state when window loses focus
    const handleWindowBlur = () => {
      if (spaceKeyPressedRef.current && toolBeforeSpaceRef.current) {
        spaceKeyPressedRef.current = false
        setCurrentTool(toolBeforeSpaceRef.current)
        toolBeforeSpaceRef.current = null
      }
    }

    // Use capture phase to intercept spacebar before browser default behavior
    window.addEventListener('keydown', handleKeyDown, true)
    window.addEventListener('keyup', handleKeyUp, true)
    window.addEventListener('blur', handleWindowBlur)

    // Also prevent default scroll behavior for space key at document level
    const preventSpaceScroll = (e: KeyboardEvent) => {
      if (e.key === ' ' && !isInputActive(e)) {
        e.preventDefault()
      }
    }

    // Helper to check if typing in input
    const isInputActive = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      return (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.getAttribute('contenteditable') === 'true'
      )
    }

    document.addEventListener('keydown', preventSpaceScroll, { passive: false })

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true)
      window.removeEventListener('keyup', handleKeyUp, true)
      window.removeEventListener('blur', handleWindowBlur)
      document.removeEventListener('keydown', preventSpaceScroll)
    }
  }, [currentTool])

  return (
    <div className="flex-1 flex flex-col bg-gray-50">
      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Tool buttons - only show if not readOnly */}
            {!readOnly && (
              <>
                <button
                  onClick={() => {
                    setCurrentTool('select')
                    if (onViewSettingsChange) {
                      onViewSettingsChange({
                        scale,
                        offset,
                        showGrid,
                        autoAIEnabled,
                        keypointRadius,
                        rectangleWidth,
                        showOnlySelected,
                        currentTool: 'select',
                      })
                    }
                  }}
                  className={`p-2 rounded ${currentTool === 'select' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
                  title="Select Tool"
                >
                  <CursorArrowRaysIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    setCurrentTool('pan')
                    if (onViewSettingsChange) {
                      onViewSettingsChange({
                        scale,
                        offset,
                        showGrid,
                        autoAIEnabled,
                        keypointRadius,
                        rectangleWidth,
                        showOnlySelected,
                        currentTool: 'pan',
                      })
                    }
                  }}
                  className={`p-2 rounded ${currentTool === 'pan' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
                  title="Pan Tool"
                >
                  <HandRaisedIcon className="w-5 h-5" />
                </button>

                <div className="w-px h-6 bg-gray-300" />
              </>
            )}

            {/* Zoom controls */}
            <button onClick={zoomOut} className="p-2 text-gray-600 hover:bg-gray-100 rounded">
              <MagnifyingGlassMinusIcon className="w-5 h-5" />
            </button>
            <span className="text-sm text-gray-600 min-w-[60px] text-center">
              {Math.round(scale * 100)}%
            </span>
            <button onClick={zoomIn} className="p-2 text-gray-600 hover:bg-gray-100 rounded">
              <MagnifyingGlassPlusIcon className="w-5 h-5" />
            </button>
            <button
              onClick={fitToView}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded"
              title="Fit to Screen"
            >
              <HomeIcon className="w-5 h-5" />
            </button>
            {selectedAnnotationId && (
              <button
                onClick={fitToBoundingBox}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                title="Fit to Bounding Box"
              >
                <ViewfinderCircleIcon className="w-5 h-5" />
              </button>
            )}

            <div className="w-px h-6 bg-gray-300" />

            {/* Grid toggle */}
            <button
              onClick={toggleGrid}
              className={`p-2 rounded ${showGrid ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
              title="Toggle Grid"
            >
              <Squares2X2Icon className="w-5 h-5" />
            </button>

            {/* Show only selected toggle */}
            {!readOnly && (
              <button
                onClick={() => {
                  const newShowOnlySelected = !showOnlySelected
                  setShowOnlySelected(newShowOnlySelected)
                  if (onViewSettingsChange) {
                    onViewSettingsChange({
                      scale,
                      offset,
                      showGrid,
                      autoAIEnabled,
                      keypointRadius,
                      rectangleWidth,
                      showOnlySelected: newShowOnlySelected,
                      currentTool,
                    })
                  }
                }}
                className={`p-2 rounded ${showOnlySelected ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
                title={showOnlySelected ? 'Show all keypoints' : 'Show only selected'}
              >
                {showOnlySelected ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            )}

            {/* Download */}
            <button
              onClick={downloadImage}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded"
              title="Download Image"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
            </button>

            {/* Auto-label button */}
            {!readOnly && isAutoLabelFeatureEnabled && selectedAnnotationId && (
              <button
                onClick={handleAutoLabel}
                disabled={isAutoLabeling}
                className={`p-2 transition-colors ${
                  isAutoLabeling
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded'
                }`}
                title="Auto-label keypoints using AI"
              >
                <SparklesIcon className={`w-5 h-5 ${isAutoLabeling ? 'animate-pulse' : ''}`} />
              </button>
            )}
          </div>

          <div className="flex items-center gap-4">
            {/* Auto-label toggle */}
            {!readOnly && isAutoLabelFeatureEnabled && (
              <button
                onClick={() => {
                  const newAutoAIEnabled = !autoAIEnabled
                  setAutoAIEnabled(newAutoAIEnabled)
                  if (onViewSettingsChange) {
                    onViewSettingsChange({
                      scale,
                      offset,
                      showGrid,
                      autoAIEnabled: newAutoAIEnabled,
                      keypointRadius,
                      rectangleWidth,
                      showOnlySelected,
                      currentTool,
                    })
                  }
                }}
                disabled={isAutoLabeling}
                className={`p-2 transition-colors rounded flex items-center gap-1 ${
                  isAutoLabeling
                    ? 'opacity-50 cursor-not-allowed'
                    : autoAIEnabled
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      : 'text-gray-600 hover:bg-gray-100'
                }`}
                title={
                  isAutoLabeling
                    ? 'Processing...'
                    : autoAIEnabled
                      ? 'Auto-label is ON - New bounding boxes will be auto-labeled'
                      : 'Auto-label is OFF - Click to enable automatic keypoint prediction'
                }
              >
                <SparklesIcon className={`w-5 h-5 ${isAutoLabeling ? 'animate-pulse' : ''}`} />
                <span className="text-xs font-medium">AUTO</span>
              </button>
            )}

            {/* Fullscreen toggle */}
            {onToggleFullscreen && (
              <button
                onClick={onToggleFullscreen}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                title={isFullscreen ? 'Exit Fullscreen (F11)' : 'Enter Fullscreen (F11)'}
              >
                {isFullscreen ? (
                  <ArrowsPointingInIcon className="w-5 h-5" />
                ) : (
                  <ArrowsPointingOutIcon className="w-5 h-5" />
                )}
              </button>
            )}

            {/* Instructions */}
            <div className="relative group">
              <button className="p-2 text-gray-400 hover:text-gray-600">
                <QuestionMarkCircleIcon className="w-5 h-5" />
              </button>

              {/* Tooltip */}
              <div className="absolute right-0 top-full mt-2 w-64 p-3 bg-gray-800 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="space-y-1 leading-relaxed">
                  <div className="pb-2 mt-0.5">
                    {!readOnly && (
                      <>
                        <div>
                          <span className="font-semibold">Shift+Drag:</span> Draw bounding box
                        </div>
                        <div>
                          <span className="font-semibold">Drag corners:</span> Resize bounding box
                        </div>
                        <div>
                          <span className="font-semibold">Ctrl+Click:</span> Toggle inferred
                        </div>
                        <div>
                          <span className="font-semibold">Right-click:</span> Delete keypoint
                        </div>
                        <div>
                          <span className="font-semibold">Shift+Right-click:</span> Delete bbox
                        </div>
                      </>
                    )}
                    <div>
                      <span className="font-semibold">Scroll:</span> Zoom (Pan mode only)
                    </div>
                    {!readOnly && (
                      <div>
                        <span className="font-semibold">Auto:</span> Auto-predict on new bounding
                        box
                      </div>
                    )}
                  </div>
                  <div className="pt-2 mb-0.5 border-t border-gray-600">
                    {!readOnly && (
                      <>
                        <div>
                          <span className="font-semibold">Q/W:</span> Previous/Next keypoint
                        </div>
                        <div>
                          <span className="font-semibold">Hold Space:</span> Temporarily use Pan
                          tool
                        </div>
                        <div>
                          <span className="font-semibold">Ctrl+S:</span> Save annotations
                        </div>
                      </>
                    )}
                    <div>
                      <span className="font-semibold">A/S:</span> Previous/Next image
                    </div>
                    <div>
                      <span className="font-semibold">F11:</span> Enter/Exit Fullscreen
                    </div>
                  </div>
                </div>
                <div className="absolute -top-2 right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Canvas container */}
      <div
        ref={containerRef}
        className="flex-1 relative overflow-hidden"
        tabIndex={0}
        onKeyDown={e => {
          // Prevent page scroll when pressing space
          if (e.key === ' ' || e.code === 'Space') {
            e.preventDefault()
            e.stopPropagation()
          }
        }}
        onKeyUp={e => {
          // Also prevent on keyup to be extra sure
          if (e.key === ' ' || e.code === 'Space') {
            e.preventDefault()
            e.stopPropagation()
          }
        }}
      >
        <canvas
          ref={canvasRef}
          className="absolute inset-0"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{
            cursor: currentTool === 'pan' ? (isPanning ? 'grabbing' : 'grab') : 'crosshair',
          }}
        />
      </div>
    </div>
  )
}
